import { useState } from 'react';
import { AlertOctagon, Phone, MapPin, X, Check } from 'lucide-react';

interface SOSButtonProps {
  onEmergency: () => void;
  /** Keep SOS off the navigation bottom sheet on mobile */
  placement?: 'default' | 'navigation';
}

export function SOSButton({ onEmergency, placement = 'default' }: SOSButtonProps) {
  const [showConfirm, setShowConfirm] = useState(false);
  const [countdown, setCountdown] = useState<number | null>(null);
  const [activated, setActivated] = useState(false);

  const handlePress = () => {
    setShowConfirm(true);
  };

  const handleConfirm = () => {
    setShowConfirm(false);
    setCountdown(3);
    
    const timer = setInterval(() => {
      setCountdown(prev => {
        if (prev === 1) {
          clearInterval(timer);
          setActivated(true);
          onEmergency();
          
          // Auto-hide after 5 seconds
          setTimeout(() => {
            setActivated(false);
            setCountdown(null);
          }, 5000);
          
          return null;
        }
        return prev! - 1;
      });
    }, 1000);
  };

  const handleCancel = () => {
    setShowConfirm(false);
    setCountdown(null);
  };

  if (activated) {
    return (
      <div className="fixed inset-0 bg-red-600 z-[9999] flex flex-col items-center justify-center p-8 animate-pulse">
        <AlertOctagon className="w-24 h-24 text-white mb-6" />
        <h1 className="text-[32px] text-white font-bold mb-4 text-center">
          EMERGENCY ACTIVATED
        </h1>
        <p className="text-white text-[18px] text-center mb-8">
          Emergency services have been notified
          <br />
          Your location is being shared
        </p>
        <div className="w-full max-w-sm space-y-4">
          <div className="flex items-center gap-3 p-4 bg-white/20 rounded-[12px]">
            <Phone className="w-6 h-6 text-white" />
            <div className="text-white">
              <p className="text-[14px]">Calling Emergency Services</p>
              <p className="text-[12px] opacity-80">Police & GO7 Support</p>
            </div>
          </div>
          <div className="flex items-center gap-3 p-4 bg-white/20 rounded-[12px]">
            <MapPin className="w-6 h-6 text-white" />
            <div className="text-white">
              <p className="text-[14px]">Location Shared</p>
              <p className="text-[12px] opacity-80">Emergency contact & GO7 HQ</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (countdown !== null) {
    return (
      <div className="fixed inset-0 bg-black/90 z-[9999] flex items-center justify-center">
        <div className="text-center">
          <div className="w-32 h-32 rounded-full bg-red-600 flex items-center justify-center mb-6 mx-auto">
            <span className="text-[64px] text-white font-bold">{countdown}</span>
          </div>
          <p className="text-white text-[20px] mb-4">Activating emergency...</p>
          <button
            onClick={handleCancel}
            className="px-8 py-3 rounded-[12px] bg-white text-[#111111] hover:bg-gray-100 transition-colors"
          >
            Cancel
          </button>
        </div>
      </div>
    );
  }

  if (showConfirm) {
    return (
      <div className="fixed inset-0 bg-black/80 z-[9999] flex items-center justify-center p-8">
        <div className="bg-white rounded-[16px] p-8 max-w-md w-full">
          <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mb-6 mx-auto">
            <AlertOctagon className="w-8 h-8 text-red-600" />
          </div>
          
          <h2 className="text-[24px] font-bold text-center mb-3">
            Activate Emergency SOS?
          </h2>
          
          <p className="text-[14px] text-[#6B6B6B] text-center mb-6">
            This will immediately:
          </p>

          <div className="space-y-3 mb-8">
            <div className="flex items-center gap-3 p-3 bg-red-50 rounded-[12px]">
              <Phone className="w-5 h-5 text-red-600" />
              <p className="text-[14px]">Call emergency services & GO7 support</p>
            </div>
            <div className="flex items-center gap-3 p-3 bg-red-50 rounded-[12px]">
              <MapPin className="w-5 h-5 text-red-600" />
              <p className="text-[14px]">Share your live location</p>
            </div>
            <div className="flex items-center gap-3 p-3 bg-red-50 rounded-[12px]">
              <AlertOctagon className="w-5 h-5 text-red-600" />
              <p className="text-[14px]">Alert your emergency contact</p>
            </div>
          </div>

          <div className="flex gap-3">
            <button
              onClick={handleCancel}
              className="flex-1 h-12 rounded-[12px] bg-gray-100 text-[#111111] hover:bg-gray-200 transition-colors flex items-center justify-center gap-2"
            >
              <X className="w-5 h-5" />
              Cancel
            </button>
            <button
              onClick={handleConfirm}
              className="flex-1 h-12 rounded-[12px] bg-red-600 text-white hover:bg-red-700 transition-colors flex items-center justify-center gap-2"
            >
              <Check className="w-5 h-5" />
              Confirm
            </button>
          </div>

          <p className="text-[12px] text-[#6B6B6B] text-center mt-4">
            Only use in genuine emergencies
          </p>
        </div>
      </div>
    );
  }

  const fabClass =
    placement === 'navigation'
      ? 'fixed top-[4.5rem] right-3 w-12 h-12 rounded-full bg-red-600 shadow-lg z-30 flex items-center justify-center touch-manipulation'
      : 'fixed bottom-24 right-8 w-16 h-16 rounded-full bg-red-600 shadow-lg hover:shadow-xl transition-all z-50 flex items-center justify-center group hover:scale-110 touch-manipulation';

  return (
    <button
      onClick={handlePress}
      className={fabClass}
      aria-label="Emergency SOS"
    >
      <AlertOctagon className={`${placement === 'navigation' ? 'w-6 h-6' : 'w-8 h-8'} text-white`} />
      {placement === 'default' && (
      <span className="absolute -top-10 right-0 bg-red-600 text-white text-[12px] px-3 py-1 rounded-full opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
        Emergency SOS
      </span>
      )}
    </button>
  );
}
